import {
  Button,
  Checkbox,
  Input,
  Message,
  SelectPicker,
  Table,
  Tag,
  TagPicker,
  Uploader,
  useToaster,
} from 'rsuite';
import React, { Dispatch, SetStateAction, useEffect } from 'react';

import * as groupsApi from '../../api/groups';
import CheckIcon from '@rsuite/icons/Check';
import DeleteIcon from '@rsuite/icons/Trash';
import { FormModal } from '../../common/FormModal';
import RequestsAndStudentsSearcher from '../../common/RequestsAndStudentsSearcher';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import { useConfirm } from '../../common/useConfirm';

const { Column, HeaderCell, Cell } = Table;
type Props = {
  groupId: string;
  setGroupId: Dispatch<SetStateAction<string>>;
};

export default function GroupStudentsListModal({ groupId, setGroupId }: Props) {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  let cellClicked = false;
  const [groupStudentsList, setGroupStudentsList] = React.useState<any[]>([]);
  const [studentsList, setStudentsList] = React.useState<
    {
      id: any;
      email: any;
      firstName: string;
      secondName: string;
    }[]
  >([]);
  const [requestsList, setRequestsList] = React.useState<
    {
      id: any;
      email: any;
      firstName: string;
      secondName: string;
    }[]
  >([]);
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const selection = ({ id }: { id: string }) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(r => r !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  // useEffect(() => {
  //   if (!queue.length || !foundRequests.length) {
  //     return;
  //   }
  //   setFoundRequests(foundRequests.filter(r => !queue.find((q: any) => q.requestId === r.id)));
  //   setFoundStudents(foundStudents.filter(s => !queue.find((q: any) => q.id === s.id)));
  // }, [queue]);

  useEffect(() => {
    if (!groupId) {
      return;
    }
    getGroupStudentsList()
      .catch(console.error)
      .then(() => setLoading(false));
  }, [groupId]);

  const getGroupStudentsList = async () => {
    setLoading(true);
    const students = await groupsApi.getStudents(groupId);
    setGroupStudentsList(students.students);
    setLoading(false);
  };

  return (
    <>
      {ConfirmDialog}
      <FormModal
        open={groupId.length > 0}
        onClose={() => setGroupId('')}
        title="Список студентів групи"
        footer={
          <Button onClick={() => setGroupId('')} appearance="primary">
            Закрити
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <RequestsAndStudentsSearcher
            requestsList={requestsList}
            studentsList={studentsList}
            setRequestsList={setRequestsList}
            setStudentsList={setStudentsList}
          />
          <Button
            style={{ width: 32, height: 32, alignSelf: 'end', margin: 10 }}
            onClick={async () => {
              if (requestsList.length || studentsList.length) {
                try {
                  await groupsApi.addStudents(
                    groupId,
                    requestsList.map(r => r.id),
                    studentsList.map(s => s.id)
                  );
                  setStudentsList([]);
                  setRequestsList([]);
                  await getGroupStudentsList();
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Студентів додано до групи
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                } catch (e: any) {
                  console.error(e);
                  const errorMessage =
                    e.response?.data?.message ||
                    'Не вдалося оновити групу. Спробуйте пізніше';
                  toaster.push(
                    <Message type="error" showIcon closable>
                      {errorMessage}
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                }
              }
            }}
          >
            <CheckIcon />
          </Button>
        </div>
        {selectedRows.length > 0 && (
          <Button
            onClick={async () => {
              const result = await confirm({
                message: `Ви впевнені що хочете прибрати з групи ${selectedRows.length} студент${selectedRows.length > 1 ? 'ів' : 'а'}?`,
                destructive: true,
                confirmText: 'Прибрати',
              });
              if (!result) {
                return;
              }
              try {
                await groupsApi.removeStudents(groupId, selectedRows);
                await getGroupStudentsList();
                setSelectedRows([]);
                toaster.push(
                  <Message type="success" showIcon closable>
                    Студентів видалено з групи
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
              } catch (e: any) {
                console.error(e);
                const errorMessage =
                  e.response?.data?.message ||
                  'Не вдалося оновити групу. Спробуйте пізніше';
                toaster.push(
                  <Message type="error" showIcon closable>
                    {errorMessage}
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
              }
            }}
          >
            Прибрати з групи
          </Button>
        )}
        <Table
          loading={loading}
          autoHeight={true}
          renderLoading={renderTableLoading}
          renderEmpty={renderTableEmpty}
          onRowClick={rowData => {
            if (cellClicked) {
              cellClicked = false;
              return;
            }
            selection({ id: rowData.id });
          }}
          // sortColumn={'createdAt'}
          // sortType={sortOrder}
          // onSortColumn={(sortColumn, sortType) => {
          //   setSortOrder(sortType || 'asc');
          //   getQueue().catch(console.error);
          // }}
          data={groupStudentsList}
        >
          <Column width={64} resizable={true}>
            <HeaderCell>
              <Button
                appearance="link"
                onClick={() => {
                  if (selectedRows.length === groupStudentsList.length) {
                    setSelectedRows([]);
                  } else {
                    setSelectedRows(groupStudentsList.map(s => s.id));
                  }
                }}
              >
                <CheckIcon />
              </Button>
            </HeaderCell>
            <Cell style={{ display: 'flex', alignItems: 'center' }}>
              {rowData => (
                <Checkbox
                  checked={selectedRows.includes(rowData.id)}
                  onCheckboxClick={() => {
                    selection({ id: rowData.id });
                  }}
                />
              )}
            </Cell>
          </Column>
          <Column width={200} resizable={true}>
            <HeaderCell>Пошта</HeaderCell>
            <Cell dataKey="email" />
          </Column>
          <Column width={120} resizable={true}>
            <HeaderCell>Тип</HeaderCell>
            <Cell dataKey="typeName" />
          </Column>
          <Column width={180} resizable={true}>
            <HeaderCell>Ім'я</HeaderCell>
            <Cell dataKey="firstName" />
          </Column>
          <Column width={180} resizable={true}>
            <HeaderCell>Прізвище</HeaderCell>
            <Cell dataKey="secondName" />
          </Column>
          <Column width={180} sortable={true} resizable={true}>
            <HeaderCell>У групі з</HeaderCell>
            <Cell dataKey="createdAt">
              {rowData =>
                new Date(rowData.groups_students.createdAt).toLocaleString()
              }
            </Cell>
          </Column>
          <Column width={80} resizable={true}>
            <HeaderCell>Дії</HeaderCell>
            <Cell>
              {rowData => (
                <>
                  <Button
                    appearance="subtle"
                    onClick={async () => {
                      cellClicked = true;
                      const result = await confirm({
                        message: `Ви впевнені, що хочете видалити з групи ${rowData.email} (${rowData.firstName} ${rowData.secondName})?`,
                        destructive: true,
                        confirmText: 'Видалити',
                      });
                      if (!result) {
                        return;
                      }
                      try {
                        await groupsApi.removeStudents(groupId, [rowData.id]);
                        await getGroupStudentsList();
                        setSelectedRows(
                          selectedRows.filter(sR => sR !== rowData.id)
                        );
                        toaster.push(
                          <Message type="success" showIcon closable>
                            Студентів видалено з групи
                          </Message>,
                          {
                            placement: 'topEnd',
                            duration: 1500,
                          }
                        );
                      } catch (e: any) {
                        console.error(e);
                        const errorMessage =
                          e.response?.data?.message ||
                          'Не вдалося оновити групу. Спробуйте пізніше';
                        toaster.push(
                          <Message type="error" showIcon closable>
                            {errorMessage}
                          </Message>,
                          {
                            placement: 'topEnd',
                            duration: 1500,
                          }
                        );
                      }
                    }}
                  >
                    <DeleteIcon />
                  </Button>
                </>
              )}
            </Cell>
          </Column>
        </Table>
        {selectedRows.length ? <p>Виділено: {selectedRows.length}</p> : <></>}
      </FormModal>
    </>
  );
}
